import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Calendar as CalendarIcon, Clock, User, Mail, Phone, MessageSquare, Edit2, Check, X } from 'lucide-react';
import SlotTemplateManager from './SlotTemplateManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminCalendar = () => {
  const [timeSlots, setTimeSlots] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('single'); // 'single', 'bulk', or 'bookings'

  // Single slot creation
  const [newSlot, setNewSlot] = useState({
    date: '',
    start_time: '',
    end_time: '',
    available: true,
    notes: ''
  });

  // Bulk creation
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);  // Changed to array for Calendar
  const [bulkNotes, setBulkNotes] = useState('');

  // Note editing
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');

  useEffect(() => {
    fetchTimeSlots();
    fetchAppointments();
  }, []);

  const fetchTimeSlots = async () => {
    try {
      const response = await axios.get(`${API}/admin/timeslots`);
      setTimeSlots(response.data);
    } catch (error) {
      console.error('Failed to fetch time slots:', error);
    }
  };

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API}/admin/appointments`);
      setAppointments(response.data);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    }
  };



  const handleAddSlot = async () => {
    if (!newSlot.date || !newSlot.start_time || !newSlot.end_time) {
      toast.error('Заполните все поля');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/timeslots`, newSlot);
      toast.success('Слот добавлен');
      setNewSlot({ date: '', start_time: '', end_time: '', available: true, notes: '' });
      fetchTimeSlots();
    } catch (error) {
      toast.error('Ошибка добавления слота');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCreate = async () => {
    if (selectedSlots.length === 0) {
      toast.error('Выберите хотя бы один временной слот');
      return;
    }
    if (!selectedDates || selectedDates.length === 0) {
      toast.error('Выберите хотя бы одну дату');
      return;
    }

    // Convert Date objects to YYYY-MM-DD format
    const dates = selectedDates.map(date => {
      const d = new Date(date);
      return d.toISOString().split('T')[0];
    });

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/timeslots/bulk`, {
        time_slots: selectedSlots,
        dates: dates,
        notes: bulkNotes
      });
      toast.success(`Создано ${response.data.count} слотов`);
      setSelectedDates([]);
      setBulkNotes('');
      fetchTimeSlots();
    } catch (error) {
      toast.error('Ошибка массового создания');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm('Удалить этот временной слот?')) return;

    try {
      await axios.delete(`${API}/admin/timeslots/${slotId}`);
      toast.success('Слот удален');
      fetchTimeSlots();
    } catch (error) {
      toast.error('Ошибка удаления');
    }
  };

  const handleToggleAvailability = async (slotId, currentAvailability) => {
    try {
      await axios.put(`${API}/admin/timeslots/${slotId}`, {
        available: !currentAvailability
      });
      toast.success('Доступность обновлена');
      fetchTimeSlots();
    } catch (error) {
      toast.error('Ошибка обновления');
    }
  };

  const handleUpdateNotes = async (slotId, notes) => {
    try {
      await axios.put(`${API}/admin/timeslots/${slotId}`, { notes });
      toast.success('Примечание обновлено');
      setEditingNoteId(null);
      fetchTimeSlots();
    } catch (error) {
      toast.error('Ошибка обновления примечания');
    }
  };

  const startEditingNote = (slotId, currentNotes) => {
    setEditingNoteId(slotId);
    setEditingNoteText(currentNotes || '');
  };

  const cancelEditingNote = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };

  const handleUpdateAppointmentStatus = async (appointmentId, newStatus) => {
    try {
      await axios.put(`${API}/admin/appointments/${appointmentId}`, {
        status: newStatus
      });
      toast.success('Статус обновлен');
      fetchAppointments();
      fetchTimeSlots();
    } catch (error) {
      toast.error('Ошибка обновления статуса');
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm('Удалить эту запись?')) return;

    try {
      await axios.delete(`${API}/admin/appointments/${appointmentId}`);
      toast.success('Запись удалена');
      fetchAppointments();
      fetchTimeSlots();
    } catch (error) {
      toast.error('Ошибка удаления');
    }
  };

  const getAppointmentForSlot = (slotId) => {
    return appointments.find(a => a.slot_id === slotId);
  };

  return (
    <div className="min-h-screen px-6 py-12">
      <div className="container mx-auto max-w-6xl">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="outline" size="icon">
                <ArrowLeft size={18} />
              </Button>
            </Link>
            <h1 className="text-4xl font-bold" style={{ color: 'var(--text-primary)' }}>
              <CalendarIcon className="inline mr-2" size={36} />
              Управление Календарем
            </h1>
          </div>
        </div>

        {/* Tab Selector */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab('single')}
            variant={activeTab === 'single' ? 'default' : 'outline'}
          >
            Одиночное создание
          </Button>
          <Button
            onClick={() => setActiveTab('bulk')}
            variant={activeTab === 'bulk' ? 'default' : 'outline'}
          >
            Массовое создание
          </Button>
          <Button
            onClick={() => setActiveTab('bookings')}
            variant={activeTab === 'bookings' ? 'default' : 'outline'}
          >
            Бронирования ({appointments.length})
          </Button>
        </div>

        {/* Single Slot Creation */}
        {activeTab === 'single' && (
          <Card className="glass-card mb-6">
            <CardHeader>
              <CardTitle style={{ color: 'var(--text-primary)' }}>Добавить Временной Слот</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block mb-2 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    Дата
                  </label>
                  <Input
                    type="date"
                    value={newSlot.date}
                    onChange={(e) => setNewSlot({ ...newSlot, date: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block mb-2 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      Начало
                    </label>
                    <Input
                      type="time"
                      value={newSlot.start_time}
                      onChange={(e) => setNewSlot({ ...newSlot, start_time: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      Конец
                    </label>
                    <Input
                      type="time"
                      value={newSlot.end_time}
                      onChange={(e) => setNewSlot({ ...newSlot, end_time: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  Примечание (необязательно)
                </label>
                <Input
                  value={newSlot.notes}
                  onChange={(e) => setNewSlot({ ...newSlot, notes: e.target.value })}
                  placeholder="Например: Онлайн консультация"
                />
              </div>
              <Button onClick={handleAddSlot} disabled={loading} className="btn-primary">
                <Plus className="mr-2" size={18} />
                Добавить
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Bulk Slot Creation */}
        {activeTab === 'bulk' && (
          <div className="space-y-6 mb-6">
            <SlotTemplateManager onSlotsChange={setSelectedSlots} />

            <Card className="admin-card">
              <CardHeader>
                <CardTitle style={{ color: 'var(--admin-text-primary)' }}>Выбор дат</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="block mb-2 text-sm font-medium" style={{ color: 'var(--admin-text-primary)' }}>
                      Выберите даты (можно выбрать несколько)
                    </label>
                    <div className="border rounded-lg p-4" style={{ borderColor: 'var(--admin-border)', background: 'var(--admin-bg-primary)' }}>
                      <Calendar
                        mode="multiple"
                        selected={selectedDates}
                        onSelect={setSelectedDates}
                        className="rounded-md"
                      />
                    </div>
                    <p className="text-xs mt-2" style={{ color: 'var(--admin-text-secondary)' }}>
                      Выбрано дат: <strong>{selectedDates?.length || 0}</strong>
                    </p>
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-medium" style={{ color: 'var(--admin-text-primary)' }}>
                      Общее примечание для всех слотов (необязательно)
                    </label>
                    <Input
                      value={bulkNotes}
                      onChange={(e) => setBulkNotes(e.target.value)}
                      placeholder="Например: Онлайн консультация"
                      className="admin-input"
                    />
                  </div>
                  <div className="p-4 rounded" style={{ background: 'var(--admin-bg-tertiary)' }}>
                    <p style={{ color: 'var(--admin-text-secondary)' }}>
                      <strong>Будет создано:</strong> {selectedSlots.length} × {selectedDates?.length || 0} = {selectedSlots.length * (selectedDates?.length || 0)} слотов
                    </p>
                  </div>
                  <Button onClick={handleBulkCreate} disabled={loading} className="admin-button w-full">
                    <Plus className="mr-2" size={18} />
                    {loading ? 'Создание...' : 'Создать слоты'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <Card className="glass-card">
            <CardHeader>
              <CardTitle style={{ color: 'var(--text-primary)' }}>
                Записи на Консультацию ({appointments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {appointments.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)' }}>Нет записей</p>
              ) : (
                <div className="space-y-4">
                  {appointments.map((appointment) => {
                    const slot = timeSlots.find(s => s.id === appointment.slot_id);
                    return (
                      <div
                        key={appointment.id}
                        className="p-4 rounded"
                        style={{ background: 'var(--admin-bg-secondary)', border: '1px solid var(--admin-border)' }}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-semibold text-lg" style={{ color: 'var(--admin-text-primary)' }}>
                              {appointment.name}
                            </h4>
                            <p className="text-sm" style={{ color: 'var(--admin-text-secondary)' }}>
                              {appointment.email} {appointment.phone && `• ${appointment.phone}`}
                            </p>
                          </div>
                          <span
                            className="px-3 py-1 rounded text-sm font-medium"
                            style={{
                              background:
                                appointment.status === 'confirmed'
                                  ? 'rgba(34, 197, 94, 0.2)'
                                  : appointment.status === 'cancelled'
                                    ? 'rgba(239, 68, 68, 0.2)'
                                    : 'rgba(250, 204, 21, 0.2)',
                              color:
                                appointment.status === 'confirmed'
                                  ? '#16a34a'
                                  : appointment.status === 'cancelled'
                                    ? '#dc2626'
                                    : '#ca8a04'
                            }}
                          >
                            {appointment.status === 'confirmed'
                              ? 'Подтверждено'
                              : appointment.status === 'cancelled'
                                ? 'Отменено'
                                : 'Ожидание'}
                          </span>
                        </div>
                        {slot && (
                          <div className="mb-3 p-2 rounded" style={{ background: 'var(--admin-bg-primary)' }}>
                            <p className="text-sm" style={{ color: 'var(--admin-text-secondary)' }}>
                              <CalendarIcon size={14} className="inline mr-1" />
                              {new Date(slot.date).toLocaleDateString('ru-RU')} • {slot.start_time} - {slot.end_time}
                            </p>
                          </div>
                        )}
                        {appointment.message && (
                          <p className="text-sm mb-3" style={{ color: 'var(--admin-text-secondary)' }}>
                            <strong>Сообщение:</strong> {appointment.message}
                          </p>
                        )}
                        <div className="flex gap-2">
                          {appointment.status !== 'confirmed' && (
                            <Button
                              onClick={() => handleUpdateAppointmentStatus(appointment.id, 'confirmed')}
                              size="sm"
                              style={{ background: '#16a34a' }}
                            >
                              Подтвердить
                            </Button>
                          )}
                          {appointment.status !== 'cancelled' && (
                            <Button
                              onClick={() => handleUpdateAppointmentStatus(appointment.id, 'cancelled')}
                              size="sm"
                              variant="outline"
                            >
                              Отменить
                            </Button>
                          )}
                          <Button
                            onClick={() => handleDeleteAppointment(appointment.id)}
                            size="sm"
                            variant="destructive"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Time Slots List - Only show when not in bookings tab */}
        {activeTab !== 'bookings' && (
          <Card className="glass-card mb-6">
            <CardHeader>
              <CardTitle style={{ color: 'var(--text-primary)' }}>
                Временные Слоты ({timeSlots.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {timeSlots.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)' }}>Нет временных слотов</p>
              ) : (
                <div className="space-y-3">
                  {timeSlots
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .map((slot) => {
                      const appointment = getAppointmentForSlot(slot.id);
                      const isBooked = !!appointment;

                      return (
                        <div
                          key={slot.id}
                          className="p-4 rounded"
                          style={{
                            background: isBooked ? 'rgba(34, 197, 94, 0.1)' : slot.available ? 'var(--admin-bg-secondary)' : 'rgba(239, 68, 68, 0.1)',
                            border: `1px solid ${isBooked ? 'rgba(34, 197, 94, 0.3)' : slot.available ? 'var(--admin-border)' : 'rgba(239, 68, 68, 0.3)'}`
                          }}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-4">
                              <CalendarIcon size={20} style={{ color: 'var(--admin-info)' }} />
                              <div>
                                <p className="font-medium" style={{ color: 'var(--admin-text-primary)' }}>
                                  {new Date(slot.date).toLocaleDateString('ru-RU', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </p>
                                <p className="text-sm" style={{ color: 'var(--admin-text-secondary)' }}>
                                  <Clock size={14} className="inline mr-1" />
                                  {slot.start_time} - {slot.end_time}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {!isBooked && (
                                <Button
                                  onClick={() => handleToggleAvailability(slot.id, slot.available)}
                                  size="sm"
                                  variant={slot.available ? 'outline' : 'default'}
                                >
                                  {slot.available ? 'Доступен' : 'Занят'}
                                </Button>
                              )}
                              <Button
                                onClick={() => handleDeleteSlot(slot.id)}
                                size="sm"
                                variant="destructive"
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>

                          {/* Notes */}
                          {editingNoteId === slot.id ? (
                            <div className="flex gap-2 mb-2">
                              <Input
                                value={editingNoteText}
                                onChange={(e) => setEditingNoteText(e.target.value)}
                                placeholder="Примечание..."
                                className="flex-1"
                              />
                              <Button size="sm" onClick={() => handleUpdateNotes(slot.id, editingNoteText)}>
                                <Check size={16} />
                              </Button>
                              <Button size="sm" variant="outline" onClick={cancelEditingNote}>
                                <X size={16} />
                              </Button>
                            </div>
                          ) : (
                            slot.notes && (
                              <div className="flex items-center gap-2 mb-2">
                                <p className="text-sm flex-1" style={{ color: 'var(--admin-text-secondary)' }}>
                                  📝 {slot.notes}
                                </p>
                                <Button size="sm" variant="ghost" onClick={() => startEditingNote(slot.id, slot.notes)}>
                                  <Edit2 size={14} />
                                </Button>
                              </div>
                            )
                          )}
                          {!slot.notes && editingNoteId !== slot.id && (
                            <Button size="sm" variant="ghost" onClick={() => startEditingNote(slot.id, '')}>
                              <Plus size={14} className="mr-1" />
                              Добавить примечание
                            </Button>
                          )}

                          {/* Client Data */}
                          {isBooked && appointment && (
                            <div className="mt-3 p-3 rounded" style={{ background: 'var(--admin-bg-primary)', border: '1px solid var(--admin-border)' }}>
                              <p className="text-sm font-semibold mb-2" style={{ color: 'var(--admin-info)' }}>
                                ✅ ЗАБРОНИРОВАНО
                              </p>
                              <div className="space-y-1">
                                <p className="text-sm flex items-center gap-2" style={{ color: 'var(--admin-text-primary)' }}>
                                  <User size={14} />
                                  <strong>Клиент:</strong> {appointment.name}
                                </p>
                                <p className="text-sm flex items-center gap-2" style={{ color: 'var(--admin-text-primary)' }}>
                                  <Mail size={14} />
                                  <strong>Email:</strong> {appointment.email}
                                </p>
                                {appointment.phone && (
                                  <p className="text-sm flex items-center gap-2" style={{ color: 'var(--admin-text-primary)' }}>
                                    <Phone size={14} />
                                    <strong>Телефон:</strong> {appointment.phone}
                                  </p>
                                )}
                                {appointment.message && (
                                  <p className="text-sm flex items-start gap-2" style={{ color: 'var(--admin-text-primary)' }}>
                                    <MessageSquare size={14} className="mt-0.5" />
                                    <span><strong>Сообщение:</strong> {appointment.message}</span>
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminCalendar;
